// frontend/src/components/product/ProductCard.jsx

import React, { memo } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";




import "./ProductCard.css";

import { getProductCardSwatchSummary, safeText } from "./swatches/productSwatches";
import ProductSwatch from "./swatches/ProductSwatch";
import ProductImage, {
  CATALOGUE_PRODUCT_IMAGE_SIZES,
} from "./images/ProductImage";
import {
  appendMtmParams,
  getMtmSelectionActionLabel,
  normalizeMtmStep,
} from "../utils/madeToMeasureJourney";
import {
  getCustomerProductName,
  getIncludedLabel,
} from "../utils/serviceCustomerCopy";
import { getProductDisplayPrice } from "../utils/productPricing";

const PLACEHOLDER_IMAGE = "https://placehold.co/900x1200?text=Academy+Costumes+London";

function toPositiveInteger(value) {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatPricingUnit(value = "", { fromPricingUnit = false } = {}) {
  const raw = safeText(value).replace(/\s+/g, " ");
  const normalized = raw.toLowerCase();

  if (!normalized) return "";
  if (normalized === "each" || normalized === "per each") return "each";
  if (["piece", "item", "unit", "button"].includes(normalized)) return "each";
  if (normalized === "garment") return "per garment";
  if (normalized === "per garment") return "per garment";
  if (normalized.startsWith("per ")) return normalized;

  return fromPricingUnit ? raw : "";
}

function getDisplayPricingUnit(product = {}, servicePricingContext = null) {
  const pricingUnit = formatPricingUnit(product?.pricingUnit, {
    fromPricingUnit: true,
  });

  if (pricingUnit) return pricingUnit;

  const unitLabel = formatPricingUnit(product?.unitLabel);
  if (unitLabel) return unitLabel;

  return formatPricingUnit(servicePricingContext?.defaultPricingUnit, {
    fromPricingUnit: true,
  });
}

function getInclusionText(
  product = {},
  pricingUnit = "",
  servicePricingContext = null,
) {
  const explicitQuantity = toPositiveInteger(product?.includedQuantity);
  const isPerGarment = pricingUnit === "per garment";

  const includedQuantity =
    explicitQuantity ||
    (isPerGarment
      ? toPositiveInteger(servicePricingContext?.defaultIncludedQuantity)
      : 0);

  const includedItemLabel =
    safeText(product?.includedItemLabel) ||
    (isPerGarment ? safeText(servicePricingContext?.defaultIncludedItemLabel) : "");

  if (!includedQuantity || !includedItemLabel) return "";

  return getIncludedLabel(includedQuantity, includedItemLabel);
}

function getPrimaryBadge(product) {
  if (product?.exclusive) return "Exclusive";
  if (product?.limitedEdition) return "Limited Edition";
  if (product?.isEditorsPick) return "Editors’ Pick";
  if (product?.isNewArrival) return "New Arrival";
  if (product?.isVintage) return "Vintage";
  if (product?.isTrending) return "Trending";

  return "";
}

function getIsOutOfStock(product) {
  if (product?.hasStock === true) return false;
  if (product?.hasStock === false) return true;

  const candidates = [
    product?.available,
    product?.stockAvailable,
    product?.countInStock,
    product?.lengthAvailable,
    product?.sampleAvailable,
  ];

  const firstNumeric = candidates.find((value) => {
    const number = Number(value);
    return Number.isFinite(number);
  });

  if (firstNumeric !== undefined) {
    return Number(firstNumeric) <= 0;
  }

  return false;
}

function getSoldOutLabel(product) {
  if (
    product?.isVintage ||
    product?.limitedEdition ||
    product?.exclusive ||
    product?.isCustomMade
  ) {
    return "Sold";
  }

  return "Currently unavailable";
}

function ProductImageLink({ to, disabled, ariaLabel, children }) {
  if (disabled) {
    return (
      <div
        className="shop-product-card__image-link"
        aria-label={ariaLabel}
        aria-disabled="true"
      >
        {children}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className="shop-product-card__image-link"
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

function ProductNameLink({ to, disabled, ariaLabel, children }) {
  if (disabled) {
    return (
      <span
        className="shop-product-card__name shop-product-card__name--static"
        aria-label={ariaLabel}
      >
        {children}
      </span>
    );
  }

  return (
    <Link to={to} className="shop-product-card__name" aria-label={ariaLabel}>
      {children}
    </Link>
  );
}

function ProductCardComponent({
  product,
  shopKey,
  mtmStep = "",
  servicePricingContext = null,
  imageSizes = CATALOGUE_PRODUCT_IMAGE_SIZES,
}) {
  const productName =
    getCustomerProductName(product, servicePricingContext) || "Untitled product";
  const productBrand = safeText(product?.brand);

  const resolvedShopKey = safeText(shopKey || product?.shopKey);
  const resolvedIdentifier = safeText(product?.slug || product?._id);

  const hasValidRoute = Boolean(resolvedShopKey && resolvedIdentifier);
  const normalizedMtmStep = normalizeMtmStep(mtmStep);
  const mtmActionLabel = normalizedMtmStep
    ? getMtmSelectionActionLabel(normalizedMtmStep)
    : "";

  const detailsHref = appendMtmParams(
    `/shop/${encodeURIComponent(resolvedShopKey)}/product/${encodeURIComponent(
      resolvedIdentifier,
    )}`,
    normalizedMtmStep,
  );

  const displayPrice = getProductDisplayPrice(product);
  const pricingUnit = getDisplayPricingUnit(product, servicePricingContext);
  const displayPriceWithUnit = displayPrice && pricingUnit
    ? `${displayPrice} ${pricingUnit}`
    : displayPrice;
  const inclusionText = getInclusionText(
    product,
    pricingUnit,
    servicePricingContext,
  );
  const badge = getPrimaryBadge(product);
  const isOutOfStock = getIsOutOfStock(product);
  const soldOutLabel = getSoldOutLabel(product);

  const linkLabel = `View ${productName}`;
  const imageAlt = productBrand
    ? `${productName} by ${productBrand}`
    : productName;

  const swatchSummary = getProductCardSwatchSummary(product);
  const showSwatches = swatchSummary.visibleItems.length > 0;

  return (
    <motion.article
      className={clsx("shop-product-card", {
        "shop-product-card--sold-out": isOutOfStock,
        "shop-product-card--disabled": !hasValidRoute,
      })}
      whileHover={hasValidRoute ? { y: -4 } : undefined}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      <ProductImageLink
        to={detailsHref}
        disabled={!hasValidRoute}
        ariaLabel={linkLabel}
      >
        <div className="shop-product-card__image-wrap">
          <ProductImage
            product={product}
            fallback={PLACEHOLDER_IMAGE}
            alt={imageAlt}
            className="shop-product-card__image"
            sizes={imageSizes}
            loading="lazy"
            decoding="async"
          />

          {badge && !isOutOfStock ? (
            <div className="shop-product-card__badge">
              <Sparkles size={14} aria-hidden="true" />
              <span>{badge}</span>
            </div>
          ) : null}

          {isOutOfStock ? (
            <div className="shop-product-card__sold-badge">{soldOutLabel}</div>
          ) : null}
        </div>
      </ProductImageLink>

      <div className="shop-product-card__meta">
        <ProductNameLink
          to={detailsHref}
          disabled={!hasValidRoute}
          ariaLabel={linkLabel}
        >
          {productName}
        </ProductNameLink>

        {showSwatches ? (
          <div
            className="shop-product-card__colorways"
            aria-label={swatchSummary.ariaLabel}
          >
            <div className="shop-product-card__swatches">
              {swatchSummary.visibleItems.map((item) => (
                <ProductSwatch
                  key={item.key}
                  swatch={item.swatch}
                  className="shop-product-card__swatch"
                  label={item.ariaLabel || item.detailLabel || item.label}
                  title={item.detailLabel || item.ariaLabel || item.label}
                />
              ))}

              {swatchSummary.hiddenCount > 0 ? (
                <span className="shop-product-card__swatch-count">
                  +{swatchSummary.hiddenCount}
                </span>
              ) : null}
            </div>

            {swatchSummary.label ? (
              <span className="shop-product-card__colorway-label">
                {swatchSummary.label}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="shop-product-card__footer">
          {displayPriceWithUnit || inclusionText ? (
            <div className="shop-product-card__price-block">
              {displayPriceWithUnit ? (
                <div className="shop-product-card__price">
                  {displayPriceWithUnit}
                </div>
              ) : null}

              {inclusionText ? (
                <div className="shop-product-card__inclusion">
                  {inclusionText}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasValidRoute ? (
            <Link
              to={detailsHref}
              className={clsx(
                "shop-product-card__details-link",
                mtmActionLabel && "shop-product-card__details-link--mtm",
              )}
              aria-label={mtmActionLabel || linkLabel}
            >
              {mtmActionLabel ? <span>{mtmActionLabel}</span> : null}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      </div>
    </motion.article>
  );
}

const ProductCard = memo(ProductCardComponent);

export default ProductCard;
